'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { CheckCircle2, Key } from 'lucide-react';
import { createClient, getAuthRedirectUrl } from '@/lib/supabase/client';
import { mapAuthError } from '@/lib/auth-errors';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const isVerified = searchParams.get('verified') === 'true';
  const queryEmail = searchParams.get('email') || '';

  // Auto-resolve verified email from query param or localStorage
  const getInitialEmail = () => {
    if (queryEmail) return queryEmail;
    if (typeof window !== 'undefined') {
      return (
        localStorage.getItem('kheldrop_verified_email') ||
        localStorage.getItem('kheldrop_last_signup_email') ||
        ''
      );
    }
    return '';
  };

  const [email, setEmail] = useState(getInitialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const supabase = createClient();

  // If email came via search params or was verified, ensure state is updated
  useEffect(() => {
    const resolved = getInitialEmail();
    if (resolved && !email) {
      setEmail(resolved);
    }
  }, [searchParams]);

  // When email is already auto-filled (e.g. after verification), auto-focus password
  // so browser key/password managers (Google Password Manager, iCloud Keychain, 1Password)
  // immediately prompt or offer to auto-fill the stored password.
  useEffect(() => {
    if (email && passwordInputRef.current) {
      const timer = setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [email]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cleanEmail = email.trim().toLowerCase();
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });

      if (authError) {
        const mapped = mapAuthError(authError.message);
        setError(mapped.message);
        return;
      }

      if (data?.user && typeof window !== 'undefined') {
        localStorage.setItem('kheldrop_auth_user', JSON.stringify(data.user));
        if (data.session) {
          localStorage.setItem('kheldrop_token', data.session.access_token);
        }
        localStorage.setItem('kheldrop_verified_email', cleanEmail);
        window.dispatchEvent(new CustomEvent('kheldrop_auth_change', { detail: data.user }));
      }

      const isCompleted =
        typeof window !== 'undefined' &&
        localStorage.getItem('kheldrop_onboarding_completed') === 'true';

      if (!isCompleted) {
        router.push('/?onboarding=true');
      } else {
        router.push(redirectTo);
      }
      router.refresh();
    } catch (err) {
      const mapped = mapAuthError(err);
      setError(mapped.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setLoading(true);
    setError('');

    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl(`/auth/callback?redirect=${encodeURIComponent(redirectTo)}`),
        },
      });

      if (oauthError) {
        const mapped = mapAuthError(oauthError.message);
        setError(mapped.message);
        setLoading(false);
      }
    } catch (err) {
      const mapped = mapAuthError(err);
      setError(mapped.message);
      setLoading(false);
    }
  }

  return (
    <>
      <div className="auth-card-header">
        <h2>Welcome back</h2>
        <p>Sign in to your KhelDrop account to find games and book turfs near you.</p>
      </div>

      {/* Verification Acknowledgment Banner */}
      {isVerified && (
        <div
          className="auth-alert auth-alert-success"
          style={{
            background: 'rgba(22, 163, 74, 0.08)',
            border: '1px solid rgba(22, 163, 74, 0.25)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            color: '#15803d',
            boxShadow: '0 2px 8px rgba(22, 163, 74, 0.08)',
          }}
        >
          <div
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(22, 163, 74, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '1px',
            }}
          >
            <CheckCircle2 size={18} color="#16a34a" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '14px', color: '#15803d' }}>
              Email Verified Successfully!
            </div>
            <div style={{ fontSize: '12.5px', color: '#166534', marginTop: '3px', lineHeight: 1.45 }}>
              Your email <strong>{email || 'address'}</strong> has been confirmed. Sign in using your password or device key storage to continue.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="auth-alert auth-alert-error">
          <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Google OAuth */}
      <button
        type="button"
        className="auth-oauth-btn"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z" />
          <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
          <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
          <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
        </svg>
        Continue with Google
      </button>

      <div className="auth-divider">
        <div className="auth-divider-line" />
        <span className="auth-divider-text">or</span>
        <div className="auth-divider-line" />
      </div>

      {/* Auto-fill Key Storage Hint */}
      {email && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#5b21b6',
            background: 'rgba(109, 40, 217, 0.08)',
            border: '1px solid rgba(109, 40, 217, 0.22)',
            padding: '8px 12px',
            borderRadius: '8px',
            marginBottom: '14px',
          }}
        >
          <Key size={14} color="#6d28d9" style={{ flexShrink: 0 }} />
          <span>Email pre-filled. Tap password to use saved password or key storage.</span>
        </div>
      )}

      {/* Email/Password Form configured for browser key & credential storage autofill */}
      <form
        id="login-form"
        name="login"
        method="post"
        action="#"
        autoComplete="on"
        onSubmit={handleSubmit}
      >
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="login-email">Email address</label>
          <input
            ref={emailInputRef}
            id="login-email"
            name="username"
            className="auth-input"
            type="email"
            placeholder="you@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="username webauthn"
            autoCapitalize="none"
            spellCheck="false"
            required
            autoFocus={!email}
          />
        </div>

        <div className="auth-form-group">
          <div className="auth-label-row">
            <label className="auth-label" htmlFor="login-password" style={{ margin: 0 }}>
              Password
            </label>
            <Link href="/forgot-password" className="auth-inline-link" style={{ fontSize: '12px' }}>
              Forgot password?
            </Link>
          </div>
          <div className="auth-password-wrapper">
            <input
              ref={passwordInputRef}
              id="login-password"
              name="password"
              className="auth-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password webauthn"
              required
              autoFocus={!!email}
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading ? (
            <>
              <span className="auth-spinner" />
              Signing in…
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="auth-footer">
        Don&apos;t have an account?{' '}
        <Link href="/signup">Create one</Link>
      </div>
    </>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="auth-spinner" style={{ margin: '40px auto', display: 'block' }} />}>
      <LoginForm />
    </Suspense>
  );
}
